import { createContext, useContext, useEffect, useState } from 'react';
import { getResolvedTheme } from './theme-provider.domain';
import type { Theme } from './theme-provider.domain';

export type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

export const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function useTheme() {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export interface UseThemeProviderArgs {
  defaultTheme: Theme;
  storageKey: string;
}

export function useThemeProvider({ defaultTheme, storageKey }: UseThemeProviderArgs) {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const resolved = getResolvedTheme(theme, systemPrefersDark);
    root.classList.add(resolved);
  }, [theme]);

  const setTheme = (next: Theme) => {
    localStorage.setItem(storageKey, next);
    setThemeState(next);
  };

  return {
    value: { theme, setTheme },
  };
}
